import { Outcome } from "@ethossoftworks/outcome"
import {
    CacheEndpoint,
    mapCacheEndpointFromEndpointPath,
    mapToUpdateCacheSettingDto,
    UserCacheControl,
} from "@model/cache/UserCacheControl"
import { User } from "@model/user/User"
import { CacheService } from "@service/cache/CacheService"
import { v4 as uuidv4 } from "uuid"
import { db } from "./IndexDbWrapper"

export class UserCacheModule {
    constructor(private userCacheService: CacheService) {}
    private threshold = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

    fetchSessionCache = async (user: User) => {
        try {
            var localCache = await SessionCacheSettingStorageManager.getCacheSettings(user)
            let sessionId: string | undefined = undefined

            if (localCache) {
                sessionId = localCache.sessionID

                //also clean any old cache that might be there from different users
                CachedDataStorageManager.clearDifferentUserCachedData(sessionId)
                SessionCacheSettingStorageManager.clearDifferentUserSessionCache(user)

                if (this.isOverExpiringThreshold(localCache.createdDateTime)) {
                    this.cleanCurrentSessionCache(user, sessionId)
                    sessionId = uuidv4()
                }
            } else {
                //if there is no local cache, create a new one
                sessionId = uuidv4()
                //also clean any old cache that might be there
                SessionCacheSettingStorageManager.clearAllSessionCacheSettings()
                CachedDataStorageManager.clearAllCachedData()
            }

            const outcome = await this.userCacheService.fetchUserCache(sessionId)
            SessionCacheSettingStorageManager.setCacheSettings(user, outcome)
        } catch (error) {
            console.error("Failed to fetch session cache.", error)
        }
    }

    updateSessionCache = async (user: User) => {
        try {
            var localCache = await SessionCacheSettingStorageManager.getCacheSettings(user)
            if (localCache) {
                await this.userCacheService.updateUserCache(
                    localCache.sessionID,
                    localCache.cacheSettings?.values.map((x) => mapToUpdateCacheSettingDto(x)) ?? []
                )
            }
        } catch (error) {
            console.error("Failed to update session cache.", error)
        }
    }

    isValid = async (user: User, path: string, fetchAlways: boolean = false): Promise<boolean> => {
        try {
            if (fetchAlways) await this.fetchSessionCache(user)

            var localCache = await SessionCacheSettingStorageManager.getCacheSettings(user)
            if (localCache) {
                let endpoint = mapCacheEndpointFromEndpointPath(path)
                var setting = localCache.cacheSettings?.values.find((s) => s.endpoint === endpoint)
                if (setting) {
                    return setting.isCacheUpToDate
                }
            }

            return false
        } catch (error) {
            console.error("Failed to check if cache is valid.", error)
            return false
        }
    }

    getCachedResponse = async (user: User, path: string): Promise<Outcome<undefined | object>> => {
        try {
            var localCache = await SessionCacheSettingStorageManager.getCacheSettings(user)
            if (localCache) {
                let endpoint = mapCacheEndpointFromEndpointPath(path)
                if (endpoint) {
                    var res = await CachedDataStorageManager.getCachedData(endpoint, localCache.sessionID)

                    return formatOutcome(res !== null ? (JSON.parse(res) as { value: any }) : null)
                }
            }
            return Outcome.error("Cache not found")
        } catch (error) {
            console.error("Failed to get cached response.", error)
            return Outcome.error("Failed to get cached response.")
        }
    }

    setCachedResponse = async (user: User, path: string, value: any | null) => {
        try {
            var localCache = await SessionCacheSettingStorageManager.getCacheSettings(user)
            if (localCache) {
                let endpoint = mapCacheEndpointFromEndpointPath(path)
                if (endpoint) {
                    CachedDataStorageManager.setCachedData(endpoint, localCache.sessionID, value)
                    SessionCacheSettingStorageManager.updateEndpointState(user, endpoint)
                }
            }
        } catch (e) {
            console.error("Failed to set cached response.", e)
        }
    }

    private isOverExpiringThreshold = (createdDateTime: Date): boolean =>
        Math.abs(new Date().getTime() - new Date(createdDateTime).getTime()) > this.threshold

    private cleanCurrentSessionCache = (user: User, sessionId: string) => {
        SessionCacheSettingStorageManager.setCacheSettings(user, null)
        this.cleanCurrentSessionCacheEndpoint(user, sessionId)
    }

    private cleanCurrentSessionCacheEndpoint = (user: User, sessionId: string) => {
        Object.values(CacheEndpoint).map((endpoint) => {
            CachedDataStorageManager.setCachedData(endpoint, sessionId, null)
        })
    }
}

export class SessionCacheSettingStorageManager {
    private static readonly sessionCacheSettingsName = "SessionCacheSettings"

    static getCacheSettings = async (user: User): Promise<UserCacheControl | null> => {
        var res = await db.get<string>(`${this.sessionCacheSettingsName}_${user?.id}-${user?.companyId}`)
        if (res) {
            return JSON.parse(res) as UserCacheControl
        }
        return null
    }

    static setCacheSettings = (user: User, value: UserCacheControl | null) => {
        if (value) {
            db.set(`${this.sessionCacheSettingsName}_${user?.id}-${user?.companyId}`, value)
        } else {
            db.delete(`${this.sessionCacheSettingsName}_${user?.id}-${user?.companyId}`)
        }
    }

    static updateEndpointState = async (user: User, endpoint: CacheEndpoint) => {
        var localCache = await this.getCacheSettings(user)
        if (localCache) {
            var setting = localCache.cacheSettings?.values.find((s) => s.endpoint === endpoint)
            if (setting) {
                setting.isCacheUpToDate = true
                this.setCacheSettings(user, localCache)
            } else {
                console.log(`Endpoint : ${endpoint} not found in cache settings.`)
            }
        }
    }

    static clearDifferentUserSessionCache = async (user: User) => {
        let keys = await db.keys()
        Object.keys(keys)
            .filter((key) => key.includes(this.sessionCacheSettingsName))
            .filter((key) => !key.includes(`${user.id}-${user.companyId}`))
            .forEach((key) => db.delete(key))
    }

    static clearAllSessionCacheSettings = async () => {
        let keys = await db.keys()
        Object.keys(keys)
            .filter((key) => key.includes(this.sessionCacheSettingsName))
            .forEach((key) => db.delete(key))
    }
}

export class CachedDataStorageManager {
    private static readonly sessionCacheName = "SessionCacheData"

    static getCachedData = async (endpoint: CacheEndpoint, SessionId: string): Promise<any | null> => {
        var res = await db.get<any | null>(`${this.sessionCacheName}_${endpoint}_${SessionId}`)
        return res ?? null
    }

    static setCachedData = (endpoint: CacheEndpoint, SessionId: string, value: any | null) => {
        if (value) {
            db.set(`${this.sessionCacheName}_${endpoint}_${SessionId}`, value)
        } else {
            db.delete(`${this.sessionCacheName}_${endpoint}_${SessionId}`)
        }
    }

    static clearAllCachedData = async () => {
        let keys = await db.keys()
        Object.keys(keys)
            .filter((key) => key.includes(this.sessionCacheName))
            .forEach((key) => db.delete(key))
    }

    static clearDifferentUserCachedData = async (sessionId: string) => {
        let keys = await db.keys()
        Object.keys(keys)
            .filter((key) => key.includes(this.sessionCacheName))
            .filter((key) => !key.includes(sessionId))
            .forEach((key) => db.delete(key))
    }
}

// export class cachedDataStorageManager {
//     private static readonly sessionCacheName = "SessionCacheData"

//     static getCachedData = (endpoint: CacheEndpoint, SessionId: string): object | null => {
//         var res = localStorage.getItem(`${this.sessionCacheName}_${endpoint}_${SessionId}`)
//         return res ? JSON.parse(res) : null
//     }

//     static setCachedData = (endpoint: CacheEndpoint, SessionId: string, value: any | null) => {
//         if (value) {
//             localStorage.setItem(`${this.sessionCacheName}_${endpoint}_${SessionId}`, JSON.stringify(value))
//         } else {
//             localStorage.removeItem(`${this.sessionCacheName}_${endpoint}_${SessionId}`)
//         }
//     }

//     static clearAllCachedData = () => {
//         Object.keys(localStorage)
//             .filter((key) => key.includes(this.sessionCacheName))
//             .forEach((key) => localStorage.removeItem(key))
//     }

//     static clearDifferentUserCachedData = (sessionId: string) => {
//         Object.keys(localStorage)
//             .filter((key) => key.includes(this.sessionCacheName))
//             .filter((key) => !key.includes(sessionId))
//             .forEach((key) => localStorage.removeItem(key))
//     }
// }

export function formatOutcome(
    cachedResponse: {
        value: any[]
    } | null
): Outcome<undefined | object> {
    return cachedResponse != null
        ? Outcome.ok<undefined | object>(cachedResponse.value)
        : Outcome.error("Cache not found")
}
